import { useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useAppStore } from '../../state/useAppStore';
import { BUILTIN_SYMBOLS } from '../../../symbols/catalogue';
import {
  EMPTY_SYMBOL_CATALOGUE,
  SymbolImageCache,
  type PreparedSymbolCatalogue,
  type SymbolImageRequest,
} from '../../../symbols/images';
import { sceneSymbolRequests, symbolSampleBounds } from '../../../symbols/scene';
import { graphicKey, graphicTint } from '../../../symbols/tint';
import { isImageGraphic, symbolLibraryIdentity, type SymbolGraphic } from '../../../symbols/model';
import type { SymbolPresentation } from '../../../symbols/backing';
import { Resources, ArtworkMetrics } from './symbolResourcesContext';
export function SymbolResources({ children }: { readonly children: ReactNode }) {
  const project = useAppStore((s) => s.project);
  const cache = useMemo(() => new SymbolImageCache(), []);
  const requests = useMemo<readonly SymbolImageRequest[]>(
    () => [
      ...sceneSymbolRequests(project.layers, project.annotationLayers),
      ...project.symbolAssets.map((a) => ({
        graphic: { kind: 'asset' as const, assetId: a.id },
        tint: undefined,
      })),
      ...BUILTIN_SYMBOLS.map((b) => ({
        graphic: { kind: 'builtin' as const, id: b.id },
        tint: '#334155',
      })),
    ],
    [project.layers, project.annotationLayers, project.symbolAssets],
  );
  const signature = JSON.stringify(
    [...new Set(requests.map((r) => graphicKey(r.graphic, r.tint, r)))].sort(),
  );
  const requestsRef = useRef(requests);
  const assetsRef = useRef(project.symbolAssets);
  const assetsKey = symbolLibraryIdentity(project.symbolAssets);
  useEffect(() => {
    requestsRef.current = requests;
    assetsRef.current = project.symbolAssets;
  }, [requests, project.symbolAssets]);
  const [ready, setReady] = useState<{
    assetsKey: string;
    signature: string;
    images: PreparedSymbolCatalogue;
  } | null>(null);
  useEffect(() => () => cache.clear(), [cache, project.sources]);
  useEffect(() => {
    const abort = new AbortController();
    void cache
      .graphics(requestsRef.current, assetsRef.current, 2, abort.signal, 32)
      .then((images) => {
        if (!abort.signal.aborted) setReady({ assetsKey, signature, images });
      })
      .catch(() => {
        /* Map boundary and import action own actionable preparation failures. */
      });
    return () => abort.abort();
  }, [cache, assetsKey, project.sources, signature]);
  return (
    <ArtworkMetrics.Provider
      value={ready?.assetsKey === assetsKey ? ready.images : EMPTY_SYMBOL_CATALOGUE}
    >
      <Resources.Provider
        value={
          ready?.assetsKey === assetsKey && ready.signature === signature
            ? ready.images
            : EMPTY_SYMBOL_CATALOGUE
        }
      >
        {children}
      </Resources.Provider>
    </ArtworkMetrics.Provider>
  );
}
export function SymbolArtwork({
  graphic,
  colour = '#334155',
  tint,
  x = 15,
  y = 9,
  longEdge = 16,
  rotation = 0,
  opacity = 1,
  presentation,
}: {
  readonly graphic: SymbolGraphic;
  readonly colour?: string;
  readonly tint?: string | null;
  readonly x?: number;
  readonly y?: number;
  readonly longEdge?: number;
  readonly rotation?: number;
  readonly opacity?: number;
  readonly presentation?: SymbolPresentation;
}) {
  const images = useContext(Resources);
  const image = images.get(graphicKey(graphic, graphicTint(graphic, colour, tint), presentation));
  if (!isImageGraphic(graphic)) {
    const r = longEdge / 2;
    return (
      <g fill={colour} opacity={opacity} transform={`rotate(${rotation} ${x} ${y})`}>
        {graphic === 'circle' ? (
          <circle cx={x} cy={y} r={r} />
        ) : graphic === 'square' ? (
          <rect x={x - r} y={y - r} width={longEdge} height={longEdge} />
        ) : (
          <polygon
            points={
              graphic === 'diamond'
                ? `${x},${y - r} ${x + r},${y} ${x},${y + r} ${x - r},${y}`
                : `${x},${y - r} ${x + r},${y + r} ${x - r},${y + r}`
            }
          />
        )}
      </g>
    );
  }
  if (!image) return <g data-symbol-loading="true" />;
  const bounds = symbolSampleBounds(image, longEdge, rotation);
  return (
    <image
      href={image.preview}
      x={x - bounds.width / 2}
      y={y - bounds.height / 2}
      width={bounds.width}
      height={bounds.height}
      opacity={image.composition ? 1 : opacity}
      transform={`rotate(${rotation} ${x} ${y})`}
    />
  );
}
